import {
  BarChart3,
  Boxes,
  Clock,
  History,
  LayoutDashboard,
  MonitorSmartphone,
  Package,
  ReceiptText,
  ShoppingCart,
  Tags,
  Users,
  type LucideIcon,
} from 'lucide-react'

/** Rail groups (POS.md §18.4): the till, the branch office, the stock room. */
export type PosNavSection = 'sell' | 'office' | 'inventory'

export interface PosNavItem {
  to: string
  label: string
  icon: LucideIcon
  section: PosNavSection
  /**
   * Needed to see the page; the same key the server checks for its data (POS.md §18.7 Q5). A list: any one of them
   * (a page with several parts shows the parts the staff member may use).
   */
  permission: string | string[]
  /** Only with a real backend; the demo API has no shifts, terminals or branch office. */
  real?: boolean
  /** Reached from the register's tabs, not the rail (the page and its permission still count). */
  hideInRail?: boolean
}

export const SECTION_LABEL: Record<PosNavSection, string> = { sell: 'Sell', office: 'Branch', inventory: 'Stock room' }

/** Rail order, grouped by section. The first page the staff member may open is their landing page. */
export const POS_NAV: PosNavItem[] = [
  { to: '/', label: 'Sell', icon: ShoppingCart, section: 'sell', permission: 'pos:place_order' },
  { to: '/orders', label: 'Orders', icon: ReceiptText, section: 'sell', permission: 'pos:list' },
  { to: '/shift', label: 'Shift', icon: Clock, section: 'sell', permission: 'pos:open_shift', real: true, hideInRail: true },
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, section: 'office', permission: 'pos:list', real: true },
  { to: '/shifts', label: 'Shifts', icon: History, section: 'office', permission: ['pos_reports:shifts', 'pos:cash_in'], real: true },
  {
    to: '/reports',
    label: 'Reports',
    icon: BarChart3,
    section: 'office',
    permission: [
      'pos:view_profit_loss',
      'pos_reports:sales_by_product',
      'pos_reports:sales_by_payment_method',
      'pos_reports:shifts',
      'pos_reports:stock_values',
      'pos_reports:customers',
      'pos_reports:adjustments',
      'pos_reports:credit_repay',
    ],
    real: true,
  },
  {
    to: '/pricing',
    label: 'Prices',
    icon: Tags,
    section: 'office',
    permission: ['price_books:list', 'product_variations:update', 'offers:create'],
    real: true,
  },
  { to: '/staff', label: 'Staff', icon: Users, section: 'office', permission: 'admins:list', real: true },
  { to: '/terminals', label: 'Terminals', icon: MonitorSmartphone, section: 'office', permission: 'terminals:list', real: true },
  { to: '/inventory', label: 'Stock', icon: Boxes, section: 'inventory', permission: 'stocks:list' },
  {
    to: '/catalog',
    label: 'Catalog',
    icon: Package,
    section: 'inventory',
    permission: ['products:update', 'products:create', 'categories:update', 'brands:update', 'measurement_units:update'],
    real: true,
  },
]

export const allowedBy = (permission: string | string[], can: (permission: string) => boolean) =>
  (Array.isArray(permission) ? permission : [permission]).some(can)

export const visibleNav = (stage: string, can: (permission: string) => boolean) =>
  POS_NAV.filter((n) => (!n.real || stage !== 'demo') && allowedBy(n.permission, can))
